import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import RichTextEditor from "../components/RichTextEditor";
import DocumentImporter from "../components/DocumentImporter";
import AudioImporter from "../components/AudioImporter";
import TranscriptPaster from "../components/TranscriptPaster";
import { GenerateReportFromTranscript } from "../components/GenerateReportFromTranscript";
import ExerciseSelector from "../components/ExerciseSelector";
import {
  ClipboardList, Plus, ChevronLeft, Save, Pencil, Trash2,
  User, Calendar, FileText, Search, Eye, Download, Mic, FileUp, ClipboardPaste,
} from "lucide-react";
import { useI18n } from "../lib/i18n";
import { API } from "../App";

const INITIAL_FIELDS = [
  { key: "informations_generales", rows: 3 },
  { key: "anamnese", rows: 4 },
  { key: "evaluation_dsm5", rows: 3 },
  { key: "hypothese_diagnostic_principal", rows: 2 },
  { key: "hypotheses_diagnostics_differentiels", rows: 2 },
  { key: "hypothese_clinique_generale", rows: 3 },
  { key: "problematiques", rows: 3 },
  { key: "protocole_therapeutique", rows: 3 },
  { key: "processus_cibles", rows: 2 },
  { key: "strategies_therapeutiques", rows: 3 },
  { key: "evaluations_standardisees", rows: 2 },
  { key: "objectifs_therapeutiques", rows: 3 },
  { key: "recommandations_suivi", rows: 2 },
  { key: "notes_libres", rows: 3 },
];

const SUIVI_FIELDS = [
  { key: "agenda_objectifs", rows: 2 },
  { key: "ameliorations_progres", rows: 3 },
  { key: "revue_semaine", rows: 3 },
  { key: "humeur_symptomes", rows: 2 },
  { key: "echelles_administrees", rows: 2 },
  { key: "revue_exercices", rows: 2 },
  { key: "analyse_chaine", rows: 3 },
  { key: "analyse_chaine_abc", rows: 3 },
  { key: "analyse_chaine_sorc", rows: 3 },
  { key: "situation_pensees_emotions", rows: 3 },
  { key: "pensees_automatiques", rows: 3 },
  { key: "analyse_tcc", rows: 3 },
  { key: "interventions", rows: 3 },
  { key: "experiences_comportementales", rows: 3 },
  { key: "nouveaux_exercices", rows: 2 },
  { key: "objectifs_smart", rows: 2 },
  { key: "plan_prochaine_seance", rows: 2 },
  { key: "notes_libres", rows: 3 },
];

const SUIVI_ADO_FIELDS = [...SUIVI_FIELDS, { key: "contexte_familial_scolaire", rows: 3 }, { key: "implication_parents", rows: 2 }, { key: "developpement_motivation", rows: 2 }];
const fieldsFor = (type) => (type === "initial" ? INITIAL_FIELDS : type === "suivi_tcc_ado" ? SUIVI_ADO_FIELDS : SUIVI_FIELDS);
const typeLabel = (t, type) => (type === "initial" ? t("sessions.typeInitial") : type === "therapie_couple" ? t("sessions.typeCouple") : type === "suivi_tcc_ado" ? t("sessions.typeAdo") : t("sessions.typeSuivi"));

const SessionReportsPage = ({ user, logout }) => {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const preselectedPatient = searchParams.get("patient") || "";
  const preselectedType = ["suivi_tcc", "therapie_couple", "suivi_tcc_ado"].includes(searchParams.get("type")) ? searchParams.get("type") : "initial";
  const autoRecord = searchParams.get("record") === "1";
  const [view, setView] = useState("form"); // form | detail
  const [patients, setPatients] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(preselectedPatient);
  const [importMode, setImportMode] = useState(autoRecord ? "audio" : "paste"); // paste | audio | document
  const [sessionType, setSessionType] = useState(preselectedType);
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split("T")[0]);
  const [fields, setFields] = useState({});
  const [editingReport, setEditingReport] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [suggestedExercises, setSuggestedExercises] = useState([]);
  const [transcript, setTranscript] = useState("");

  useEffect(() => {
    fetchPatients();
    fetchReports();
  }, []);

  const fetchPatients = async () => {
    try {
      const res = await axios.get(`${API}/therapist/patients`, { withCredentials: true });
      setPatients(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/session-reports`, { withCredentials: true });
      setReports(res.data);
      const editId = searchParams.get("edit");
      const toEdit = editId && res.data.find((r) => r.report_id === editId);
      if (toEdit) openEditForm(toEdit);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const currentFields = fieldsFor(sessionType);

  const resetForm = () => {
    setSelectedPatient(preselectedPatient);
    setSessionType("initial");
    setSessionDate(new Date().toISOString().split("T")[0]);
    setFields({});
    setEditingReport(null);
    setSelectedExercises([]);
    setSuggestedExercises([]);
    setTranscript("");
    setImportMode("audio");
  };

  const openNewForm = () => {
    resetForm();
    setView("form");
  };

  const openEditForm = (report) => {
    setEditingReport(report);
    setSelectedPatient(report.patient_id);
    setSessionType(report.session_type);
    setSessionDate(report.session_date);
    setFields(report.fields || {});
    setTranscript(report.transcript || "");
    setView("form");
  };

  const openDetail = (report) => {
    setViewingReport(report);
    setView("detail");
  };

  const handleSubmit = async () => {
    if (!selectedPatient) { toast.error(t("sessions.selectPatient")); return; }
    setSubmitting(true);
    try {
      if (editingReport) {
        await axios.put(`${API}/session-reports/${editingReport.report_id}`, {
          fields, session_date: sessionDate, transcript,
        }, { withCredentials: true });
        toast.success(t("sessions.updated"));
      } else {
        await axios.post(`${API}/session-reports`, {
          patient_id: selectedPatient,
          session_type: sessionType,
          session_date: sessionDate,
          fields,
          transcript,
          exercises_to_assign: selectedExercises.map(id => ({ type_id: id })),
        }, { withCredentials: true });
        toast.success(t("sessions.created"));
      }
      resetForm();
      setView("form");
      fetchReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm(t("sessions.confirmDelete"))) return;
    try {
      await axios.delete(`${API}/session-reports/${reportId}`, { withCredentials: true });
      toast.success(t("sessions.deleted"));
      fetchReports();
      if (view === "detail") setView("list");
    } catch (e) { toast.error("Erreur"); }
  };

  const handleExportPdf = async (reportId) => {
    try {
      const res = await axios.get(`${API}/session-reports/${reportId}/pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `compte-rendu-${reportId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t("sessions.pdfExported"));
    } catch (e) {
      toast.error("Erreur lors de l'export PDF");
    }
  };

  const getPatientName = useCallback((pid) => {
    return patients.find(p => p.user_id === pid)?.name || pid;
  }, [patients]);

  // ============ DETAIL VIEW ============
  if (view === "detail" && viewingReport) {
    const r = viewingReport;
    const fieldDefs = fieldsFor(r.session_type);
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="space-y-6" data-testid="report-detail">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Button variant="ghost" onClick={() => setView("form")} data-testid="back-to-list">
              <ChevronLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleExportPdf(r.report_id)} data-testid="export-pdf-btn">
                <Download className="w-4 h-4 mr-1" /> {t("sessions.exportPdf")}
              </Button>
              <Button variant="outline" onClick={() => openEditForm(r)} data-testid="edit-report-btn">
                <Pencil className="w-4 h-4 mr-1" /> {t("sessions.edit")}
              </Button>
              <Button variant="outline" onClick={() => handleDelete(r.report_id)} className="text-red-500 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-950">
                <Trash2 className="w-4 h-4 mr-1" /> {t("sessions.delete")}
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  r.session_type === "initial"
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                    : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                }`}>
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <CardTitle className="font-heading text-xl">{r.patient_name}</CardTitle>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      r.session_type === "initial"
                        ? "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300"
                        : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                    }`}>
                      {typeLabel(t, r.session_type)}
                    </span>
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(r.session_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {fieldDefs.map(f => {
                const val = r.fields?.[f.key];
                if (!val) return null;
                return (
                  <div key={f.key} className="space-y-1">
                    <p className="text-sm font-medium text-primary">{t(`sessions.field.${f.key}`)}</p>
                    <div
                      className="text-sm text-foreground/90 bg-muted/30 p-3 rounded-lg prose prose-sm dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: val }}
                    />
                  </div>
                );
              })}
              {r.transcript && (
                <details className="rounded-lg border bg-muted/20 px-3 py-2" data-testid="detail-transcript-section">
                  <summary className="text-sm font-medium cursor-pointer flex items-center gap-2">
                    <Mic className="w-4 h-4 text-primary" /> Transcription de la séance
                    <span className="text-xs text-muted-foreground font-normal">— {r.transcript.length.toLocaleString("fr-FR")} caractères</span>
                  </summary>
                  <pre className="mt-2 text-xs font-mono whitespace-pre-wrap text-foreground/80 max-h-96 overflow-y-auto" data-testid="detail-transcript-text">{r.transcript}</pre>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // ============ FORM VIEW (Create / Edit) ============
  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="session-report-form">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-semibold">
              {editingReport ? t("sessions.editReport") : t("sessions.newReport")}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">{t("sessions.subtitle")}</p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("sessions.patient")}</label>
                <select
                  value={selectedPatient}
                  onChange={(e) => setSelectedPatient(e.target.value)}
                  disabled={!!editingReport}
                  className="w-full px-3 py-2 text-sm border rounded-lg bg-background border-border disabled:opacity-60"
                  data-testid="select-patient"
                >
                  <option value="">{t("sessions.selectPatient")}</option>
                  {patients.map(p => (
                    <option key={p.user_id} value={p.user_id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("sessions.sessionType")}</label>
                <select
                  value={sessionType}
                  onChange={(e) => { setSessionType(e.target.value); setFields({}); }}
                  disabled={!!editingReport}
                  className="w-full px-3 py-2 text-sm border rounded-lg bg-background border-border disabled:opacity-60"
                  data-testid="select-session-type"
                >
                  <option value="initial">{t("sessions.typeInitial")}</option>
                  <option value="suivi_tcc">{t("sessions.typeSuivi")}</option>
                  <option value="therapie_couple">{t("sessions.typeCouple")}</option>
                  <option value="suivi_tcc_ado">{t("sessions.typeAdo")}</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t("sessions.date")}</label>
                <Input
                  type="date"
                  value={sessionDate}
                  onChange={(e) => setSessionDate(e.target.value)}
                  data-testid="session-date"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {!editingReport && (
          <>
            <div className="flex gap-2 flex-wrap" data-testid="import-mode-toggle">
              <Button
                variant={importMode === "paste" ? "default" : "outline"}
                onClick={() => setImportMode("paste")}
                className="flex items-center gap-2"
                data-testid="import-mode-paste"
              >
                <ClipboardPaste className="w-4 h-4" /> Coller une transcription
              </Button>
              <Button
                variant={importMode === "audio" ? "default" : "outline"}
                onClick={() => setImportMode("audio")}
                className="flex items-center gap-2"
                data-testid="import-mode-audio"
              >
                <Mic className="w-4 h-4" /> Enregistrement audio
              </Button>
              <Button
                variant={importMode === "document" ? "default" : "outline"}
                onClick={() => setImportMode("document")}
                className="flex items-center gap-2"
                data-testid="import-mode-document"
              >
                <FileUp className="w-4 h-4" /> Document texte
              </Button>
            </div>

            {importMode === "paste" && (
              <TranscriptPaster
                sessionType={sessionType}
                patientId={selectedPatient}
                transcript={transcript}
                onTranscriptChange={setTranscript}
                onFieldsGenerated={(generatedFields, exercises) => {
                  setFields(prev => ({ ...prev, ...generatedFields }));
                  if (exercises && exercises.length > 0) {
                    setSuggestedExercises(exercises);
                    setSelectedExercises(exercises.map(e => e.type_id));
                  }
                }}
              />
            )}
            {importMode === "audio" && (
              <AudioImporter
                sessionType={sessionType}
                patientId={selectedPatient}
                autoStart={autoRecord}
                onFieldsGenerated={(generatedFields, exercises, sourceText) => {
                  setFields(prev => ({ ...prev, ...generatedFields }));
                  if (sourceText) setTranscript(sourceText);
                  if (exercises && exercises.length > 0) {
                    setSuggestedExercises(exercises);
                    setSelectedExercises(exercises.map(e => e.type_id));
                  }
                }}
              />
            )}
            {importMode === "document" && (
              <DocumentImporter
                sessionType={sessionType}
                patientId={selectedPatient}
                onFieldsGenerated={(generatedFields, exercises, sourceText) => {
                  setFields(prev => ({ ...prev, ...generatedFields }));
                  if (sourceText) setTranscript(sourceText);
                  if (exercises && exercises.length > 0) {
                    setSuggestedExercises(exercises);
                    setSelectedExercises(exercises.map(e => e.type_id));
                  }
                }}
              />
            )}
          </>
        )}

        {/* Exercise Selector */}
        <ExerciseSelector
          patientId={selectedPatient}
          selectedExercises={selectedExercises}
          onSelectionChange={setSelectedExercises}
          suggestedExercises={suggestedExercises}
        />

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">
              {typeLabel(t, sessionType)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {(editingReport || importMode !== "paste") && (
            <details className="group rounded-lg border bg-muted/20 px-3 py-2" open={!!transcript || !!editingReport} data-testid="transcript-section">
              <summary className="text-sm font-medium cursor-pointer flex items-center gap-2">
                <Mic className="w-4 h-4 text-primary" /> Transcription de la séance
                <span className="text-xs text-muted-foreground font-normal">{transcript ? `— ${transcript.split("\n").filter(Boolean).length} interventions · ${transcript.length} caractères` : "— optionnelle : collez-la ici (Plaud…) ou laissez l'enregistrement/import la remplir"}</span>
              </summary>
              <p className="text-xs text-muted-foreground mt-2">Horodatée et attribuée (Thérapeute / Patient). Elle alimente le dossier patient (propositions à valider) et l'e-mail de suivi.</p>
              <textarea
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                rows={transcript ? 12 : 4}
                placeholder="[00:00] Thérapeute : Comment s'est passée votre semaine ?&#10;[00:12] Patient : ..."
                className="mt-2 w-full px-3 py-2 text-sm font-mono border rounded-lg bg-background border-border"
                data-testid="transcript-textarea"
              />
              {editingReport && (
                <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs text-muted-foreground">Séance terminée ? Générez le compte rendu structuré ci-dessous à partir de cette transcription (les champs sont ensuite modifiables).</p>
                  <GenerateReportFromTranscript reportId={editingReport.report_id} transcript={transcript} hasFields={Object.values(fields).some(Boolean)}
                    onGenerated={(generatedFields, exercises) => {
                      setFields(generatedFields);
                      if (exercises?.length) { setSuggestedExercises(exercises); setSelectedExercises(exercises.map((e) => e.type_id)); }
                    }} />
                </div>
              )}
            </details>
            )}
            {currentFields.map(f => (
              <div key={f.key} className="space-y-1.5">
                <label className="text-sm font-medium">{t(`sessions.field.${f.key}`)}</label>
                <RichTextEditor
                  value={fields[f.key] || ""}
                  onChange={(val) => setFields(prev => ({ ...prev, [f.key]: val }))}
                  placeholder={t(`sessions.field.${f.key}`)}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={resetForm}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !selectedPatient} data-testid="save-report-btn">
            <Save className="w-4 h-4 mr-1" />
            {submitting ? t("exercise.saving") : (editingReport ? t("sessions.update") : t("sessions.save"))}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SessionReportsPage;
